import { AsyncPipe, CommonModule, DatePipe } from '@angular/common';
import { Component, ElementRef, EnvironmentInjector, Inject, Input, Renderer2 } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators, } from '@angular/forms';
import { Modal, ModalOptions } from '@indice/ng-components';
import { TranslocoDirective, TranslocoPipe, TranslocoService, } from '@jsverse/transloco';
import { NgxMaskDirective, NgxMaskService, provideNgxMask } from 'ngx-mask';
import { catchError, EMPTY, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { AuthService } from 'src/app/core/services/auth.service';
import { ValidationProblemDetails, } from 'src/app/services/identity-api.service';
import { WorkflowApiService } from 'src/app/services/workflow.service';
import { HttpValidationProblemDetails, PortalApiService, ProblemDetails, TotpMessageType, TotpRequestBody, TotpVerificationBody } from 'src/app/services/portal-api.service';
import { ToptCursorMoveDirective } from 'src/app/shared/directives/top-cursor-move.directive';
import { IAsideModalResponse } from '../aside-content.component';
import { ResponsiveService } from 'src/app/shared/services/responsive.service';
import { Router } from '@angular/router';
import { LanguageRouterLinkDirective } from 'src/app/shared/directives/language-router-link.directive';
import { localizeRoute } from 'src/app/shared/utilities/localizeRoutes';
import { OtpService } from 'src/app/core/services/otp.service';
import { NgxSliderModule, Options } from "@angular-slider/ngx-slider";

@Component({
  selector: 'app-make-offer-modal',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoDirective,
    TranslocoPipe,
    ReactiveFormsModule,
    NgxMaskDirective,
    NgxSliderModule,
    ToptCursorMoveDirective,
    LanguageRouterLinkDirective,
    DatePipe,
    AsyncPipe
  ],
  templateUrl: './make-offer-modal.component.html',
  styleUrl: './make-offer-modal.component.css',
  providers: [provideNgxMask()],
})
export class MakeOfferModalComponent {
  @Input() askingPrice: number | undefined;
  @Input() propertyId: string | undefined;
  @Input() isLoggedIn: boolean = false;
  @Input() propertyTitle: string | undefined;
  @Input() propertyCode: string | undefined;

  private stepSubject: Subject<number> = new Subject<number>();
  protected currentStep: number = 0;
  protected totalSteps: number = 4;
  protected offerForm: FormGroup;
  protected priceControl: FormControl;
  protected includeLoanControl: FormControl;
  protected loanAmountControl: FormControl;
  protected equityControl: FormControl = new FormControl(0);
  private priceSubject: Subject<number> = new Subject<number>();
  private includeLoanSubject: Subject<boolean> = new Subject<boolean>();
  private equitySubject: Subject<void> = new Subject<void>();
  protected equity: number = 0;
  private onPhoneFormInitSubject: Subject<void> = new Subject<void>();
  private isFirstInitPhoneForm: boolean = true;
  protected totpPhoneDisclaimerUiText: string = '';
  protected hasPhoneVerificationError: boolean = false;
  protected phoneVerificationErrorText?: string;
  protected processingRequest: boolean = false;

  public stepVisible: boolean = false;

  public isMobile$: Observable<boolean> = of(false)

  private totp$ = this._portalApi.sendTotp(new TotpRequestBody({ messageType: TotpMessageType.Offer }));

  public otpResendTimeRemaining$ = new Observable<number>;

  priceSliderOptions: Options;
  loanSliderOptions: Options;

  constructor(
    private _modal: Modal,
    private _authService: AuthService,
    private _fb: FormBuilder,
    private _translocoService: TranslocoService,
    private _workflowApi: WorkflowApiService,
    private responsiveService: ResponsiveService,
    @Inject(ModalOptions)
    private _options: ModalOptions<MakeOfferModalComponent>,
    private _router: Router,
    @Inject(EnvironmentInjector) private envInjector: EnvironmentInjector,
    private _portalApi: PortalApiService,
    private _otpService: OtpService
  ) {
    this.askingPrice = this._options?.initialState?.askingPrice as number;
    this.propertyId = this._options?.initialState?.propertyId as string;
    this.isLoggedIn = this._options?.initialState?.isLoggedIn as boolean;
    this.propertyTitle = this._options?.initialState?.propertyTitle as string;
    this.propertyCode = this._options?.initialState?.propertyCode as string;

    this.isMobile$ = this.responsiveService.isMobile$;

    this.stepSubject
      .pipe(
        tap((newStep: number) => {
          this.currentStep = newStep;
          if (this.currentStep == 3) {
            this.onPhoneFormInitSubject.next();
          }
        }),
      )
      .subscribe();

    this.offerForm = this._fb.group({
      0: this._fb.group({}),
      1: this._fb.group({
        price: [
          this.askingPrice,
          [
            Validators.required,
            Validators.min(this.getMinOfferPrice()),
            Validators.max(this.getMaxOfferPrice()),
          ],
        ],
        tnc: [false, [Validators.required, Validators.requiredTrue]],
      }),
      2: this._fb.group({
        includeLoan: [false],
        loanAmount: [undefined],
      }),
      3: this._fb.group({
        digit1: ['', Validators.required],
        digit2: ['', Validators.required],
        digit3: ['', Validators.required],
        digit4: ['', Validators.required],
        digit5: ['', Validators.required],
        digit6: ['', Validators.required],
      }),
    });

    this.priceControl = this.offerForm.get('1.price') as FormControl;
    this.includeLoanControl = this.offerForm.get(
      '2.includeLoan',
    ) as FormControl;
    this.loanAmountControl = this.offerForm.get('2.loanAmount') as FormControl;
    this.equityControl = this.offerForm.get('2.includeLoan') as FormControl;

    this.priceSubject
      .pipe(
        tap((value: number) => {
          this.equity = value - (this.loanAmountControl.value ?? (0 as number));
          this.setLoanAmountValueAndValidations();
        }),
      )
      .subscribe();

    this.includeLoanSubject
      .pipe(
        tap((value: boolean) => {
          this.equitySubject.next();
          this.setLoanAmountValueAndValidations();
        }),
      )
      .subscribe();

    this.equitySubject
      .pipe(
        tap(() => {
          this.equity =
            this.priceControl.value -
            (this.loanAmountControl.value ?? (0 as number));
        }),
      )
      .subscribe();

    this.priceControl.valueChanges
      .pipe(
        tap((value: number) => {
          this.priceSubject.next(value);
          this.equitySubject.next();
        }),
      )
      .subscribe();

    this.onPhoneFormInitSubject
      .pipe(
        tap(() => {
          if (this.isFirstInitPhoneForm) {
            this.isFirstInitPhoneForm = false;

            this.totp$.pipe(
              tap(() => {
                this.otpResendTimeRemaining$ = this._otpService.initOtpTimer(120);
              })
            ).subscribe();
          }
        }),
      )
      .subscribe();

    this.includeLoanControl.valueChanges
      .pipe(
        tap((value: boolean) => {
          this.includeLoanSubject.next(value);
        }),
      )
      .subscribe();

    this.loanAmountControl.valueChanges
      .pipe(
        tap((value: number) => {
          this.equitySubject.next();
        }),
      )
      .subscribe();

    this.totp$ = this._portalApi.sendTotp(new TotpRequestBody({ messageType: TotpMessageType.Offer }));

    this._translocoService
      .selectTranslateObject('offer-modal')
      .pipe(
        tap((t) => {
          let transText = t['totp-phone-disclaimer'];
          let maskedPhone = this.maskPhoneNumber(
            this._authService.getCurrentUser()?.profile.phone_number,
          );

          this.totpPhoneDisclaimerUiText = transText.replace(
            '{{PhoneNumber}}'.trim(),
            maskedPhone,
          );
        }),
      )
      .subscribe();

    this.priceSliderOptions = {
      floor: this.getMinOfferPrice(),
      ceil: this.getMaxOfferPrice(),
      step: 1,
      showSelectionBar: true,
    };
    this.loanSliderOptions = {};
  }

  protected onNextClick(): void {
    if (!this.isSubFormValid()) {
      this.markAllAsTouched(
        this.offerForm.get(`${this.currentStep}`) as FormGroup,
      );
      return;
    }
    if (this.currentStep === 0) {
      this.stepVisible = true;
    }
    this.stepSubject.next(this.currentStep + 1);
  }

  protected onPreviousClick(): void {
    if (this.currentStep === 1) {
      this.stepVisible = false;
    }
    this.stepSubject.next(this.currentStep - 1);
  }

  private isSubFormValid(): boolean {
    const activeSubForm = this.offerForm.get(
      `${this.currentStep}`,
    ) as FormGroup;
    const rs = activeSubForm !== null && activeSubForm.valid;
    return rs;
  }

  protected setLoanAmountValueAndValidations() {
    let loanAmountControlValue: number | undefined =
      this.loanAmountControl.value ?? undefined;
    if (this.includeLoanControl.value) {
      const minLoanAmount = this.getMinLoanAmount();
      const maxLoanAmount = this.getMaxLoanAmount();

      this.loanAmountControl.setValidators([
        Validators.required,
        Validators.min(minLoanAmount),
        Validators.max(maxLoanAmount),
      ]);

      if (
        loanAmountControlValue === undefined ||
        loanAmountControlValue < minLoanAmount ||
        loanAmountControlValue > maxLoanAmount
      ) {
        loanAmountControlValue = Math.floor(
          (minLoanAmount + maxLoanAmount) / 2,
        );
      }
    } else {
      this.loanAmountControl?.clearValidators();
      loanAmountControlValue = undefined;
    }
    this.loanAmountControl.setValue(loanAmountControlValue);
    this.loanAmountControl.updateValueAndValidity();
    this.loanSliderOptions = {
      floor: this.getMinLoanAmount(),
      ceil: this.getMaxLoanAmount(),
      step: 1,
      showSelectionBar: true,
    };
  }

  protected closeModal(): void {
    this._modal.hide({});
  }

  protected getMinOfferPrice(): number {
    const askingPrice = this.askingPrice ?? 0;
    const rs = Math.floor(askingPrice * 0.6);

    return rs;
  }
  protected getMaxOfferPrice(): number {
    const askingPrice = this.askingPrice ?? 0;
    const rs = Math.ceil(askingPrice * 1.15);

    return rs;
  }

  protected getMinLoanAmount(): number {
    const offerPrice: number = this.offerForm.get('1.price')?.value ?? 0;
    const rs = Math.floor(offerPrice * 0.1);
    return rs;
  }
  protected getMaxLoanAmount(): number {
    const offerPrice: number = this.offerForm.get('1.price')?.value ?? 0;
    const rs = Math.floor(offerPrice * 0.9);
    return rs;
  }

  protected onGoToOffersClick(): void {
    this.closeModal();
    this._router.navigate([localizeRoute(`/my-profile/buying/${this.propertyCode}/overview`, this.envInjector)]);

  }

  protected hasError(controlName: string): boolean {
    const control = this.offerForm.get(controlName) as FormControl;
    const result = control.invalid && control.touched;
    return result;
  }

  private markAllAsTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.markAllAsTouched(control);
      } else {
        control?.markAsTouched();
      }
    });
  }
  private maskPhoneNumber(phoneNumber: string | undefined): string {
    if (!phoneNumber) return '';
    const phoneSections = phoneNumber.split(' ');
    const hasCountryCode = phoneSections?.length > 1;
    const countryCode = hasCountryCode ? phoneSections[0] : '';
    const restNumber = hasCountryCode ? phoneSections[1] : phoneSections[0];
    const maskedSection = restNumber.slice(0, 6).replace(/\d/g, 'X');
    const visibleSection = restNumber.slice(6);
    return `${countryCode} ${maskedSection}${visibleSection}`;
  }

  protected resendTotp(): void {
    this.offerForm.get('3')?.reset();
    this.hasPhoneVerificationError = false;
    this.totp$.pipe(
      tap(() => {
        this.otpResendTimeRemaining$ = this._otpService.initOtpTimer(120);
      })).subscribe();
  }

  protected onPhoneSubmitClick(): void {
    this.processingRequest = true;
    const subForm = this.offerForm.get(`${this.currentStep}`) as FormGroup;
    if (!this.isSubFormValid()) {
      this.markAllAsTouched(subForm);
      this.processingRequest = false;
      return;
    }
    const token = Object.values(subForm.value).join('');
    this._portalApi
      .verifyTotp(
        new TotpVerificationBody({
          code: token,
        }),
      )
      .pipe(
        catchError((error: any) => {
          if (error instanceof ValidationProblemDetails) {
            this.phoneVerificationErrorText =
              error.errors?.['Code']?.at(0) ?? undefined;
          }

          this.hasPhoneVerificationError = true;
          this.offerForm.get('3')?.reset();
          this.processingRequest = false;

          return EMPTY;
        }),
        switchMap(() => {
          const price = this.priceControl.value;
          const loanAmount =
            this.includeLoanControl.value === true
              ? this.loanAmountControl.value
              : undefined;

          return this._workflowApi.initiatePropertyOffer({
            propertyId: this.propertyId ?? '',
            offerDetails: {
              price: price,
              loanAmount: loanAmount,
            },
          });
        }),
        catchError((error: any) => {
          let errorBody = undefined;
          if (error instanceof HttpValidationProblemDetails) {
            if (error.errors) {
              errorBody = Object.entries(error.errors)
                .map(m => m[1])
                .flat()
                .join(', ');
            }
          }
          let rs: IAsideModalResponse = {
            success: false,
            errorDesc: errorBody,
          };
          this._modal.hide(rs);

          return EMPTY;
        }),
        tap(() => {
          const nextStep = this.currentStep + 1;
          this.stepSubject.next(nextStep);
        }),
      )
      .subscribe();
  }

  public handleTitle() {
    switch (this.currentStep) {
      case 0:
        return this._translocoService.translate('offer-modal.step-0-title');//'Όλα τα βήματα'
      case 1:
        return this._translocoService.translate('offer-modal.step-1-title') + ` ${this.propertyTitle ?? ''}`//`Κάνε προσφορά για την καταχώριση ${this.propertyTitle ?? ''}`
      case 2:
        return this._translocoService.translate('offer-modal.step-2-title');//'Ενδιαφέρεσαι για δάνειο;'
      // case 3:
      //   return this._translocoService.translate('offer-modal.step-3-title');//'Στοιχεία πιστωτικής / χρεωστικής κάρτας'
      case 3:
        return this._translocoService.translate('offer-modal.step-4-title');//'Μόλις στείλαμε έναν κωδικό επαλήθευσης στο τηλέφωνό σου'
      case 4:
        return this._translocoService.translate('offer-modal.step-5-title');//'Συγχαρητήρια!'
      default:
        return '';
    }
  }
}
